import { flow, types } from 'mobx-state-tree';
import { InfoMessage } from '../types';
import { composeError } from '../utils/transforms';
import { withRootStore } from './withRootStore';
import {
  createInfoMessage,
  getInfoMessage,
  getInfoMessages,
  toggleInfoMessageActive,
  updateInfoMessage,
  listInfoMessageAttachments,
  uploadInfoMessageAttachments,
  deleteInfoMessageAttachment,
  deleteInfoMessage,
} from '../services/infoMessageApi';
import { InfoMessageAttachment } from 'types';

export const InfoMessageModel = types.model('InfoMessageModel', {
  id: types.maybe(types.string),
  title: types.string,
  body: types.maybeNull(types.string),
  churchId: types.maybeNull(types.string),
  isActive: types.boolean,
  updatedAt: types.maybe(types.string),
  status: types.maybe(types.enumeration(['draft', 'published', 'archived'])),
  attachments: types.optional(
    types.array(
      types.model('InfoMessageAttachmentModel', {
        id: types.string,
        name: types.string,
        src: types.string,
        contentType: types.maybe(types.string),
        size: types.maybe(types.number),
        createdAt: types.maybe(types.string),
      })
    ),
    []
  ),
});

export const InfoMessageStore = types
  .model('InfoMessageStore', {
    items: types.optional(types.array(InfoMessageModel), []),
    selected: types.maybe(InfoMessageModel),
    loading: types.optional(types.boolean, false),
    error: types.frozen(),
  })
  .extend(withRootStore)
  .actions((self) => {
    const fetchAll = flow(function* (search?: string, churchId?: string | null) {
      self.loading = true;
      try {
        const data: InfoMessage[] = yield getInfoMessages({ search, churchId });
        const mapped: any = data.map((m) => ({
          id: m.id != null ? String(m.id) : undefined,
          title: m.title ?? '',
          body: m.body ?? null,
          churchId: m.churchId != null ? String(m.churchId) : null,
          isActive: !!m.isActive,
          updatedAt: m.updatedAt,
          status: m.status,
          attachments: (m.attachments ?? []).map((a) => ({
            id: a.id != null ? String(a.id) : '',
            name: a.name,
            src: a.src,
            contentType: a.contentType,
            size: a.size,
            createdAt: a.createdAt,
          })),
        }));
        self.items.replace(mapped);
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      } finally {
        self.loading = false;
      }
    });

    const fetchOne = flow(function* (id: string) {
      self.loading = true;
      try {
        const m: InfoMessage = yield getInfoMessage(id);
        self.selected = {
          id: m.id != null ? String(m.id) : undefined,
          title: m.title ?? '',
          body: m.body ?? null,
          churchId: m.churchId != null ? String(m.churchId) : null,
          isActive: !!m.isActive,
          updatedAt: m.updatedAt,
          status: m.status,
          attachments: (m.attachments ?? []).map((a) => ({
            id: a.id != null ? String(a.id) : '',
            name: a.name,
            src: a.src,
            contentType: a.contentType,
            size: a.size,
            createdAt: a.createdAt,
          })),
        } as any;
        // Ensure attachments are loaded even if not included in getInfoMessage response
        try {
          const list: InfoMessageAttachment[] = yield listInfoMessageAttachments(String(m.id ?? id));
          const mapped = list.map((a) => ({
            id: a.id != null ? String(a.id) : '',
            name: a.name,
            src: a.src,
            contentType: a.contentType,
            size: a.size,
            createdAt: a.createdAt,
          }));
          if (self.selected?.id && String(self.selected.id) === String(m.id ?? id)) {
            self.selected.attachments.replace(mapped as any);
          }
        } catch (_e) {
          // ignore attachment load errors here; UI can refresh on demand
        }
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      } finally {
        self.loading = false;
      }
    });

    const save = flow(function* (payload: InfoMessage) {
      try {
        let savedId: string | undefined;
        if (payload.id) {
          const updated: InfoMessage = yield updateInfoMessage(payload.id, payload);
          savedId = updated?.id != null ? String(updated.id) : payload.id;
        } else {
          // create with forwarded status; default to draft
          const created: InfoMessage = yield createInfoMessage({
            title: payload.title,
            body: payload.body,
            churchId: payload.churchId,
            isActive: payload.isActive,
            status: payload.status ?? 'draft',
          });
          savedId = created?.id != null ? String(created.id) : undefined;
        }
        self.rootStore.errorStore.setSuccess('save_success');
        return savedId;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const setActive = flow(function* (id: string, isActive: boolean) {
      try {
        yield toggleInfoMessageActive(id, isActive);
        const idx = self.items.findIndex((x) => x.id === id);
        if (idx >= 0) self.items[idx].isActive = isActive;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const clearSelected = () => {
      self.selected = undefined;
    };

    const refreshAttachments = flow(function* (id: string, allowEmptyReplace?: boolean) {
      try {
        const list: InfoMessageAttachment[] = yield listInfoMessageAttachments(id);
        const mapped = list.map((a) => ({
          id: a.id != null ? String(a.id) : '',
          name: a.name,
          src: a.src,
          contentType: a.contentType,
          size: a.size,
          createdAt: a.createdAt,
        }));
        if (mapped.length === 0 && !allowEmptyReplace) {
          return;
        }
        const idx = self.items.findIndex((x) => x.id === String(id));
        if (idx >= 0) self.items[idx].attachments.replace(mapped as any);
        if (self.selected?.id && String(self.selected.id) === String(id))
          self.selected.attachments.replace(mapped as any);
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const uploadAttachments = flow(function* (id: string, files: File[]) {
      try {
        yield uploadInfoMessageAttachments(id, files);
        yield refreshAttachments(id, true);
        self.rootStore.errorStore.setSuccess('save_success');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const deleteAttachment = flow(function* (id: string, attachmentId: string) {
      try {
        yield deleteInfoMessageAttachment(id, attachmentId);
        yield refreshAttachments(id, true);
        self.rootStore.errorStore.setSuccess('delete_success');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const remove = flow(function* (id: string) {
      try {
        yield deleteInfoMessage(id);
        const idx = self.items.findIndex((x) => x.id === id);
        if (idx >= 0) self.items.splice(idx, 1);
        if (self.selected?.id === id) self.selected = undefined as any;
        self.rootStore.errorStore.setSuccess('delete_success');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const createDraft = flow(function* (title?: string, churchId?: string | null) {
      try {
        // minimal payload; backend sets defaults (status='draft')
        const payload: Partial<InfoMessage> = { title: title ?? '' };
        if (churchId !== undefined) payload.churchId = churchId;
        const created: InfoMessage = yield createInfoMessage(payload);
        self.selected = {
          id: created.id != null ? String(created.id) : undefined,
          title: created.title,
          body: created.body ?? null,
          churchId: created.churchId ?? null,
          isActive: !!created.isActive,
          updatedAt: created.updatedAt,
          status: created.status,
        } as any;
        return created;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    return {
      fetchAll,
      fetchOne,
      save,
      setActive,
      clearSelected,
      refreshAttachments,
      uploadAttachments,
      deleteAttachment,
      createDraft,
      remove,
    };
  });
